import React, { useState, useEffect } from 'react';
import { Badge, Popover, List, Button, Space, Typography, Empty, Divider, message } from 'antd';
import { BellOutlined, CheckOutlined } from '@ant-design/icons';
import api from '../services/api';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const { Text } = Typography;

const Notificaciones = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notificaciones, setNotificaciones] = useState([]);
  const [noLeidas, setNoLeidas] = useState(0);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);

  // Función para reproducir sonido de notificación
  const reproducirSonido = () => {
    try {
      // Crear un audio context para generar un sonido de notificación
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800; // Frecuencia en Hz
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.error('Error al reproducir sonido:', error);
    }
  };

  // Función para hacer vibrar el dispositivo (si está disponible)
  const vibrar = () => {
    if ('vibrate' in navigator) {
      try {
        // Vibrar: 200ms, pausa 100ms, vibrar 200ms
        navigator.vibrate([200, 100, 200]);
      } catch (error) {
        console.error('Error al vibrar:', error);
      }
    }
  };

  useEffect(() => {
    if (user) {
      // Inicializar el contador
      fetchNoLeidas();
      fetchNotificaciones();
      // Actualizar cada 30 segundos
      const interval = setInterval(() => {
        fetchNoLeidas();
        fetchNotificaciones();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const fetchNoLeidas = async () => {
    if (!user) return;
    
    // Para administradores sin personal_id, usar 'null' como string
    // Para otros usuarios, usar su personal_id
    const personalId = user.personal_id || (user.rol === 'admin' ? 'null' : null);
    
    if (!personalId && user.rol !== 'admin') return;
    
    try {
      const response = await api.get(`/notificaciones/no-leidas?personal_id=${personalId}`);
      const nuevoContador = response.data.total || 0;
      
      // Si hay nuevas notificaciones (el contador aumentó), reproducir sonido y vibrar
      setNoLeidas(prevContador => {
        if (nuevoContador > prevContador && prevContador > 0) {
          reproducirSonido();
          vibrar();
        }
        return nuevoContador;
      });
    } catch (error) {
      console.error('Error al obtener notificaciones no leídas:', error);
    }
  };

  const fetchNotificaciones = async () => {
    if (!user) return;
    
    // Para administradores sin personal_id, usar 'null' como string
    // Para otros usuarios, usar su personal_id
    const personalId = user.personal_id || (user.rol === 'admin' ? 'null' : null);
    
    if (!personalId && user.rol !== 'admin') return;
    
    setLoading(true);
    try {
      const response = await api.get(`/notificaciones?personal_id=${personalId}`);
      setNotificaciones(response.data || []);
    } catch (error) {
      console.error('Error al obtener notificaciones:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarcarLeida = async (notificacionId, servicioId) => {
    try {
      await api.put(`/notificaciones/${notificacionId}`);
      setNoLeidas(prev => Math.max(0, prev - 1));
      setNotificaciones(prev => 
        prev.map(n => n.id === notificacionId ? { ...n, leida: true } : n)
      );
      
      // Si tiene servicio_id, navegar a mis servicios
      if (servicioId) {
        navigate('/mis-servicios');
      }
    } catch (error) {
      message.error('Error al marcar notificación como leída');
    }
  };

  const handleMarcarTodasLeidas = async () => {
    if (!user) return;
    
    const personalId = user.personal_id || (user.rol === 'admin' ? 'null' : null);
    
    if (!personalId && user.rol !== 'admin') return;
    
    try {
      await api.put(`/notificaciones/todas?personal_id=${personalId}`);
      setNoLeidas(0);
      setNotificaciones(prev => prev.map(n => ({ ...n, leida: true })));
      message.success('Todas las notificaciones marcadas como leídas');
    } catch (error) {
      message.error('Error al marcar notificaciones');
    }
  };

  const handleClickNotificacion = (notificacion) => {
    if (!notificacion.leida) {
      handleMarcarLeida(notificacion.id, notificacion.servicio_id);
    }
    if (notificacion.servicio_id) {
      navigate('/mis-servicios');
    }
    setVisible(false);
  };

  // Mostrar notificaciones para todos los usuarios (incluyendo administradores sin personal_id)
  if (!user) {
    return null;
  }

  const content = (
    <div style={{ width: 350, maxHeight: 400, overflowY: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Text strong>Notificaciones</Text>
        {noLeidas > 0 && (
          <Button type="link" size="small" onClick={handleMarcarTodasLeidas}>
            Marcar todas como leídas
          </Button>
        )}
      </div>
      <Divider style={{ margin: '8px 0' }} />
      {notificaciones.length === 0 ? (
        <Empty description="No hay notificaciones" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <List
          dataSource={notificaciones}
          loading={loading}
          renderItem={(item) => (
            <List.Item
              style={{
                cursor: 'pointer',
                backgroundColor: !item.leida ? '#e6f7ff' : 'transparent',
                padding: '12px',
                borderRadius: 4,
                marginBottom: 8,
              }}
              onClick={() => handleClickNotificacion(item)}
            >
              <List.Item.Meta
                title={
                  <Space>
                    <Text strong={!item.leida}>{item.titulo}</Text>
                    {!item.leida && (
                      <Badge status="processing" />
                    )}
                  </Space>
                }
                description={
                  <div>
                    <div>{item.mensaje}</div>
                    <div style={{ fontSize: '11px', color: '#999', marginTop: 4 }}>
                      {dayjs(item.fecha_creacion).format('DD/MM/YYYY HH:mm')}
                    </div>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      title={null}
      trigger="click"
      visible={visible}
      onVisibleChange={setVisible}
      placement="bottomRight"
    >
      <Badge count={noLeidas} offset={[-5, 5]}>
        <BellOutlined style={{ fontSize: 20, cursor: 'pointer' }} />
      </Badge>
    </Popover>
  );
};

export default Notificaciones;

